import React, { useMemo, useCallback } from 'react';
import { AreaClosed, Line, Bar, LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { withTooltip, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { max, extent } from 'd3-array';
import { timeFormat } from 'd3-time-format';

import { datesAreTheSameDay } from 'src/utils/dates';

import './LineChart.css';

export const background = '#ffffff';
const tooltipStyles = {
    ...defaultStyles,
    background,
    color: 'black',
    padding: 12,
    borderRadius: 12,
};

// util
const formatDate = timeFormat('%B %d, %Y');

export default withTooltip(
    ({
        width,
        height,
        margin = { top: 10, right: 30, bottom: 50, left: 40 },
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipTop = 0,
        tooltipLeft = 0,
        color,
        data,
        getYValue = (d) => d.value,
        getXValue = (d) => new Date(d.date),
    }) => {
        if (width < 10) return null;

        // bounds
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        // scales
        const dateScale = useMemo(
            () =>
                scaleTime({
                    range: [margin.left, innerWidth + margin.left],
                    domain: extent(data, getXValue),
                    nice: { interval: 'day', step: 1 },
                }),
            [data, getXValue, innerWidth, margin.left]
        );

        const dataValueScale = useMemo(
            () =>
                scaleLinear({
                    range: [innerHeight, margin.top],
                    domain: [0, (max(data, getYValue) || 0) + innerHeight / 200],
                    nice: true,
                }),
            [innerHeight, margin.top, data, getYValue]
        );

        // tooltip handler
        const handleTooltip = useCallback(
            (event) => {
                const { x } = localPoint(event) || { x: 0 };
                const x0 = dateScale.invert(x);
                const index = data.findIndex((point) => datesAreTheSameDay(x0, new Date(getXValue(point))));
                let d = data[index];
                showTooltip({
                    tooltipData: d,
                    tooltipLeft: x,
                    tooltipTop: dataValueScale(getYValue(d)) + margin.top,
                });
            },
            [dateScale, data, getXValue, showTooltip, dataValueScale, getYValue, margin.top]
        );

        return (
            <div>
                <svg width={width} height={height}>
                    <rect x={0} y={0} width={width} height={height} fill="none" />
                    <LinearGradient id="area-gradient" from={color} to={color} fromOpacity={0.2} toOpacity={0} />
                    <GridRows
                        top={margin.top}
                        left={margin.left}
                        scale={dataValueScale}
                        width={innerWidth}
                        strokeDasharray="1,3"
                        stroke={'#E9E7E7'}
                        pointerEvents="none"
                    />
                    <AreaClosed
                        data={data}
                        x={(d) => dateScale(getXValue(d)) ?? 0}
                        y={(d) => (dataValueScale(getYValue(d)) ?? 0) + margin.top}
                        yScale={dataValueScale}
                        strokeWidth={0}
                        stroke={color}
                        fill="url(#area-gradient)"
                        curve={curveMonotoneX}
                    />
                    <LinePath
                        curve={curveMonotoneX}
                        stroke={color}
                        strokeWidth={2}
                        data={data}
                        x={(d) => dateScale(getXValue(d)) ?? 0}
                        y={(d) => (dataValueScale(getYValue(d)) ?? 0) + margin.top}
                    />
                    <Bar
                        x={margin.left}
                        y={margin.top}
                        width={innerWidth}
                        height={innerHeight}
                        fill="transparent"
                        onTouchStart={handleTooltip}
                        onTouchMove={handleTooltip}
                        onMouseMove={handleTooltip}
                        onMouseLeave={() => hideTooltip()}
                    />
                    <AxisBottom
                        top={height - margin.bottom + 0.1}
                        scale={dateScale}
                        numTicks={width > 600 ? (data.length > 15 ? 15 : data.length) : 5}
                        hideTicks
                        tickClassName="line-chart-tick"
                    />
                    <AxisLeft
                        top={margin.top}
                        left={margin.left}
                        scale={dataValueScale}
                        numTicks={5}
                        hideAxisLine
                        hideTicks
                        tickClassName="line-chart-tick"
                    />
                    {tooltipData && (
                        <g>
                            <Line
                                from={{ x: tooltipLeft, y: tooltipTop - 20 }}
                                to={{ x: tooltipLeft, y: tooltipTop }}
                                stroke="#FC9D29"
                                strokeWidth={2}
                                pointerEvents="none"
                            />
                            <circle
                                cx={tooltipLeft}
                                cy={tooltipTop + 1}
                                r={4}
                                fill="black"
                                fillOpacity={0.1}
                                stroke="black"
                                strokeOpacity={0.1}
                                strokeWidth={2}
                                pointerEvents="none"
                            />
                            <circle
                                cx={tooltipLeft}
                                cy={tooltipTop}
                                r={4}
                                fill="#FC9D29"
                                stroke="#FC9D29"
                                strokeWidth={2}
                                pointerEvents="none"
                            />
                        </g>
                    )}
                </svg>
                {tooltipData && (
                    <div
                        style={{
                            position: 'absolute',
                            top: tooltipTop - 90,
                            left: tooltipLeft - 60,
                            width: 120,
                            ...tooltipStyles,
                        }}
                    >
                        <p className="text-lg mb-2">{`$${getYValue(tooltipData)}`}</p>
                        <p className="text-gray-darker">{`${formatDate(getXValue(tooltipData))}`}</p>
                    </div>
                )}
            </div>
        );
    }
);
